/**
 * V197: Single Source of Truth for Verified Subscription Benefits
 * 
 * All verified dialogs (paywall, thank-you, status) use this shared data.
 * Update benefits here and all dialogs automatically sync.
 * 
 * Reference: "You're Verified" status dialog (Profile.tsx)
 */

import React from 'react';
import { TrendingUp, Shield, Users, Sparkles } from 'lucide-react';
import type { TFunction } from 'i18next';
import type { VerifiedDialogBullet } from '../components/VerifiedDialogLayout';

/**
 * The 4 core benefits of BlissNet Verified subscription
 * Used across all verified-related dialogs
 */
export const VERIFIED_BENEFITS: VerifiedDialogBullet[] = [
  {
    icon: <Shield className="w-5 h-5 text-white" />,
    label: 'Verified Badge',
    description: 'Displayed on your profile',
  },
  {
    icon: <Users className="w-5 h-5 text-white" />,
    label: 'Priority Ranking',
    description: 'Higher visibility in Explore and Network',
  },
  {
    icon: <TrendingUp className="w-5 h-5 text-white" />,
    label: '+100 Trust Score Bonus',
    description: 'Active and applied to your profile',
  },
  {
    icon: <Sparkles className="w-5 h-5 text-white" />,
    label: 'Premium Customer Service',
    description: 'Priority support and assistance',
  },
];

export function getVerifiedPaywallBenefits(t: TFunction): VerifiedDialogBullet[] {
  return [
    {
      icon: <Shield className="w-5 h-5 text-white" />,
      label: t('verified.features.badge.title'),
      description: t('verified.features.badge.description'),
    },
    {
      icon: <Users className="w-5 h-5 text-white" />,
      label: t('verified.features.priority.title'),
      description: t('verified.features.priority.description'),
    },
    {
      icon: <TrendingUp className="w-5 h-5 text-white" />,
      label: t('verified.features.trustBonus.title'),
      description: t('verified.features.trustBonus.description'),
    },
    {
      icon: <Sparkles className="w-5 h-5 text-white" />,
      label: t('verified.features.support.title'),
      description: t('verified.features.support.description'),
    },
  ];
}

/**
 * Variant-specific titles for different dialog contexts
 */
export const VERIFIED_TITLES = {
  thankyou: "You're Verified!",
  status: "You're Verified",
  paywall: "Get verified!",
} as const;

/**
 * Variant-specific subtitles
 */
export const VERIFIED_SUBTITLES = {
  thankyou: "Welcome to premium! You've unlocked exclusive features and received +100 Trust Score.",
  status: "Your premium subscription is active and you're enjoying all the benefits.",
  paywall: "Unlock premium features and boost your credibility",
} as const;

